import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Award } from "lucide-react";
import { BrandLogo } from "@/components/site/BrandLogo";

export default function CoachLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "coach") {
        toast.error("This portal is for coaches only");
        return;
      }
      toast.success(`Welcome back, coach ${u.name?.split(" ")[0] || ""}`);
      nav(u.must_change_password ? "/coach/change-password" : "/coach");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white flex items-center justify-center px-5 py-16" data-testid="coach-login">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex mb-10">
          <BrandLogo as="div" size="lg" showWordmark={true} />
        </Link>

        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">
          <Award size={12} /> // Coach Portal
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
          Take the<br /><span className="brand-blue">field</span>.
        </h1>
        <p className="mt-4 text-sm text-white/60">
          Log in to see your batches, mark attendance and reach out to parents.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-4">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Coach email" required
            data-testid="coach-login-email"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" required minLength={6}
            data-testid="coach-login-password"
            className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
          />
          <button
            type="submit" disabled={loading}
            data-testid="coach-login-submit"
            className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
          >
            {loading ? "…" : "Login"}
          </button>
        </form>

        <div className="mt-8 border-t border-white/10 pt-4 flex items-center justify-between text-xs font-mono uppercase tracking-widest">
          <Link to="/parent/login" className="text-white/40 hover:text-white">Parent →</Link>
          <Link to="/admin/login" className="text-white/40 hover:brand-orange">Admin →</Link>
        </div>
      </div>
    </div>
  );
}
